/**
 * Dokumenter — the mosque's own archive for staff and the board: folders, files, notes and links, with sharing links
 * that expire.  MKK module 15 «Intern info» + «Viktige dokumenter» (C1, 2026-10-25). Staff only; nothing here is public
 * or in the member app, except a file someone with documents.share chose to share by a link.
 *
 * Behind the `documents` capability (the menu item is absent without it, and every route answers 404).
 *   documents.read     the folder tree, the list, the search, a document's detail and its download;
 *   documents.manage   folders (new, rename, move, retention, delete an empty one), «Last opp», «Nytt notat», «Ny lenke»,
 *                      «Endre», «Last opp ny versjon», «Arkiver» (two clicks) and «Hent tilbake»;
 *   documents.share    «Del med lenke»: a link that expires (1–90 days), shown ONCE, and «Trekk tilbake» (two clicks).
 * The server says which in every answer (`can`) and decides again on each request.
 *
 * Files are encrypted at rest; a download is fetched with the session (never a bare link) and is in the activity log.
 */
import React, { useEffect, useRef, useState } from 'react';
import { download, errText, patch, post, send, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { Body as CardBody, Btn, Card, DropZone, Field, LoadError, Loading, Msg, Tag, TwoStep } from '../components/ui';
import { FormDialog } from '../components/dialog';
import { DocumentShares } from '../components/DocumentShares';

const KIND_NO: Record<string, string> = { file: 'Fil', note: 'Notat', link: 'Lenke' };
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.heic,.docx,.xlsx,application/pdf,image/jpeg,image/png,image/heic';
const MAX_MB = 15;

/** 1 234 567 → «1,2 MB». */
export function sizeText(n: number | null | undefined): string {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} kB`;
  return `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
}
const dateText = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

/** A file as base64 for the API (the bytes as they are; the server decides the type from them). */
function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (file.size > MAX_MB * 1024 * 1024) return reject(new Error(`Filen er større enn ${MAX_MB} MB.`));
    const fr = new FileReader();
    fr.onerror = () => reject(new Error('Filen kunne ikke leses.'));
    fr.onload = () => {
      const b = new Uint8Array(fr.result as ArrayBuffer); let bin = '';
      for (let i = 0; i < b.length; i += 0x8000) bin += String.fromCharCode.apply(null, Array.from(b.subarray(i, i + 0x8000)));
      resolve(btoa(bin));
    };
    fr.readAsArrayBuffer(file);
  });
}

/** Folder options for a <select>, indented by depth: «Styret», «— 2026», «— — Møter». */
function folderOptions(tree: Body[], skip?: string, maxDepth = 3): { id: string; label: string; depth: number }[] {
  const out: { id: string; label: string; depth: number }[] = [];
  const walk = (nodes: Body[]) => { for (const f of nodes) { if (f.id === skip) continue; out.push({ id: f.id, label: `${'— '.repeat(f.depth - 1)}${f.name}`, depth: f.depth }); if (f.depth < maxDepth) walk(f.children || []); } };
  walk(tree);
  return out;
}

/* ---------------------------------------------------------------- the folder tree ---------------------------------------------------------------- */

function Tree({ nodes, folder, pick }: { nodes: Body[]; folder: string; pick: (id: string) => void }) {
  return (
    <ul style={{ listStyle: 'none', margin: 0, paddingLeft: 12 }}>
      {nodes.map((f) => (
        <li key={f.id}>
          <button type="button" className="linkish" aria-pressed={folder === f.id} onClick={() => pick(f.id)}
                  style={{ fontWeight: folder === f.id ? 700 : 400, background: 'none', border: 0, padding: '3px 0', cursor: 'pointer', textAlign: 'left', color: 'inherit' }}>
            {f.name}{f.documentCount ? <span className="sub"> ({f.documentCount})</span> : null}
          </button>
          {f.children && f.children.length > 0 && <Tree nodes={f.children} folder={folder} pick={pick} />}
        </li>
      ))}
    </ul>
  );
}

type FolderForm = { id: string | null; name: string; parentId: string; retentionMonths: string };

function FolderDialog({ form, setForm, tree, maxDepth, onDone }: { form: FolderForm | null; setForm: (f: FolderForm | null) => void; tree: Body[]; maxDepth: number; onDone: (m: string) => void }) {
  const [msg, say] = useMsg();
  const [busy, setBusy] = useState(false);
  useEffect(() => { say(''); }, [form && form.id, say]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (!form) return <FormDialog open={false} title="" onClose={() => setForm(null)}>{null}</FormDialog>;
  const save = async () => {
    if (busy) return;
    const months = form.retentionMonths.trim() === '' ? null : Number(form.retentionMonths);
    const body = { name: form.name, parentId: form.parentId || null, retentionMonths: months };
    setBusy(true);
    try {
      const r = form.id ? await patch(`/documents/folders/${form.id}`, body) : await post('/documents/folders', body);
      if (!r.ok) return say(errText(r.body), 'err');
      setForm(null); onDone(form.id ? 'Mappen er lagret.' : 'Mappen er laget.');
    } finally { setBusy(false); }
  };
  return (
    <FormDialog open title={form.id ? 'Endre mappe' : 'Ny mappe'} onClose={() => setForm(null)}
      hint={`Mapper kan ligge inntil ${maxDepth} nivåer dypt. En oppbevaringstid gjelder dokumenter som lagres i mappen (og i undermapper uten egen tid): de slettes automatisk når tiden er ute.`}>
      <div className="row">
        <Field label="Navn" required className="field half">{(id) => <input id={id} maxLength={80} autoComplete="off" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />}</Field>
        <Field label="Ligger i" className="field half">{(id) => (
          <select id={id} value={form.parentId} onChange={(e) => setForm({ ...form, parentId: e.target.value })}>
            <option value="">(øverst)</option>
            {folderOptions(tree, form.id || undefined, maxDepth - 1).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>)}</Field>
      </div>
      <div className="row">
        <Field label="Oppbevaringstid (måneder)" className="field half" note="Tom: dokumentene beholdes til noen arkiverer dem.">{(id, d) => (
          <input id={id} aria-describedby={d} type="number" min={1} max={1200} inputMode="numeric" value={form.retentionMonths}
                 onChange={(e) => setForm({ ...form, retentionMonths: e.target.value })} />)}</Field>
      </div>
      <div className="row"><Btn small={false} disabled={busy} onClick={save}>{busy ? 'Lagrer …' : form.id ? 'Lagre' : 'Lag mappe'}</Btn></div>
      <Msg text={msg.text} tone={msg.tone} />
    </FormDialog>
  );
}

/* ---------------------------------------------------------------- a new document ---------------------------------------------------------------- */

type NewForm = { kind: 'file' | 'note' | 'link'; title: string; description: string; folderId: string; noteText: string; url: string; file: File | null };

function NewDialog({ form, setForm, tree, maxDepth, onDone }: { form: NewForm | null; setForm: (f: NewForm | null) => void; tree: Body[]; maxDepth: number; onDone: (m: string, id?: string) => void }) {
  const [msg, say] = useMsg();
  const [busy, setBusy] = useState(false);
  const start = useRef<NewForm | null>(null);
  useEffect(() => { say(''); start.current = form; }, [form && form.kind]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (!form) return <FormDialog open={false} title="" onClose={() => setForm(null)}>{null}</FormDialog>;
  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const base = { kind: form.kind, title: form.title, description: form.description || null, folderId: form.folderId || null };
      let body: Body;
      if (form.kind === 'note') body = { ...base, noteText: form.noteText };
      else if (form.kind === 'link') body = { ...base, url: form.url };
      else {
        if (!form.file) { say('Velg en fil.', 'err'); return; }
        let data: string;
        try { data = await readBase64(form.file); } catch (e) { say((e as Error).message, 'err'); return; }
        body = { ...base, filename: form.file.name, mime: form.file.type || null, dataBase64: data };
      }
      const r = await post('/documents', body);
      if (!r.ok) return say(errText(r.body), 'err');
      setForm(null); onDone(form.kind === 'file' ? 'Filen er lastet opp.' : form.kind === 'note' ? 'Notatet er lagret.' : 'Lenken er lagret.', r.body.document && r.body.document.id);
    } finally { setBusy(false); }
  };
  const title = form.kind === 'file' ? 'Last opp fil' : form.kind === 'note' ? 'Nytt notat' : 'Ny lenke';
  return (
    <FormDialog open title={title} wide={form.kind === 'note'} dirty={JSON.stringify({ ...form, file: null }) !== JSON.stringify({ ...start.current, file: null }) || Boolean(form.file)}
      onClose={() => setForm(null)}
      hint={form.kind === 'file' ? `PDF, bilde (JPEG, PNG, HEIC), Word (.docx) eller Excel (.xlsx), maks ${MAX_MB} MB. Filen lagres kryptert.` : undefined}>
      <div className="row">
        <Field label="Tittel" required className="field half">{(id) => <input id={id} maxLength={160} autoComplete="off" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />}</Field>
        <Field label="Mappe" className="field half">{(id) => (
          <select id={id} value={form.folderId} onChange={(e) => setForm({ ...form, folderId: e.target.value })}>
            <option value="">(ingen mappe)</option>
            {folderOptions(tree, undefined, maxDepth).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>)}</Field>
      </div>
      {form.kind === 'file' && (
        <DropZone title={form.file ? form.file.name : 'Slipp en fil her, eller klikk for å velge'} accept={ACCEPT}
                  onFile={(f) => setForm({ ...form, file: f, title: form.title || f.name.replace(/\.[^.]+$/, '') })}>
          <span className="note">{form.file ? sizeText(form.file.size) : `Maks ${MAX_MB} MB.`}</span>
        </DropZone>
      )}
      {form.kind === 'link' && (
        <div className="row"><Field label="Adresse" required className="field" note="Starter med https://">{(id, d) => (
          <input id={id} aria-describedby={d} type="url" maxLength={2000} autoComplete="off" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />)}</Field></div>
      )}
      {form.kind === 'note' && (
        <div className="row"><Field label="Notat" required className="field" note="Ren tekst, maks 20 000 tegn. Søkbart.">{(id, d) => (
          <textarea id={id} aria-describedby={d} rows={12} maxLength={20000} value={form.noteText} onChange={(e) => setForm({ ...form, noteText: e.target.value })} />)}</Field></div>
      )}
      <div className="row"><Field label="Beskrivelse" className="field">{(id) => (
        <textarea id={id} rows={2} maxLength={1000} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />)}</Field></div>
      <div className="row"><Btn small={false} disabled={busy} onClick={save}>{busy ? 'Lagrer …' : 'Lagre'}</Btn></div>
      <Msg text={msg.text} tone={msg.tone} />
    </FormDialog>
  );
}

/* ---------------------------------------------------------------- one document ---------------------------------------------------------------- */

function Detail({ id, tree, maxDepth, onClose, onChanged }: { id: string; tree: Body[]; maxDepth: number; onClose: () => void; onChanged: (m?: string) => void }) {
  const { data, error, reload } = useLoad<Body>(`/documents/${id}`, [id]);
  const [msg, say] = useMsg();
  const [edit, setEdit] = useState<Body | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setEdit(null); say(''); }, [id, say]);
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Card title="Dokument"><Loading /></Card>;
  const d: Body = data.document;
  const can: Body = data.can || {};
  const current = data.current === d.id;
  const after = (m: string) => { say(m, 'ok'); reload(); onChanged(); };

  const saveEdit = async () => {
    if (!edit || busy) return;
    setBusy(true);
    try {
      const body: Body = { title: edit.title, description: edit.description || null, folderId: edit.folderId || null };
      if (d.kind === 'note') body.noteText = edit.noteText;
      if (d.kind === 'link') body.url = edit.url;
      const r = await patch(`/documents/${d.id}`, body);
      if (!r.ok) return say(errText(r.body), 'err');
      setEdit(null); after(r.body.changed ? 'Lagret.' : 'Ingen endringer.');
    } finally { setBusy(false); }
  };
  const replace = async (file: File) => {
    if (busy) return;
    setBusy(true);
    try {
      let data64: string;
      try { data64 = await readBase64(file); } catch (e) { say((e as Error).message, 'err'); return; }
      const r = await post(`/documents/${d.id}/replace`, { filename: file.name, mime: file.type || null, dataBase64: data64 });
      if (!r.ok) return say(errText(r.body), 'err');
      say(`Versjon ${r.body.document.version} er lastet opp.`, 'ok'); onChanged(); reload();
    } finally { setBusy(false); }
  };
  const act = async (what: 'archive' | 'restore') => {
    const r = await post(`/documents/${d.id}/${what}`);
    if (!r.ok) return say(errText(r.body), 'err');
    after(what === 'archive' ? 'Dokumentet er arkivert.' : 'Dokumentet er hentet tilbake.');
  };
  const get = async (v: Body) => {
    const r = await download(`/v1/admin/documents/${encodeURIComponent(v.id)}/file`, v.filename || 'dokument');
    if (!r.ok) say(r.message || 'Filen kunne ikke lastes ned.', 'err');
  };
  return (
    <Card title={<>{d.title} {d.archivedAt && <Tag tone="grey">Arkivert</Tag>}</>}
          hint={[KIND_NO[d.kind], data.path && data.path.length ? data.path.join(' / ') : 'Ingen mappe', d.retentionUntil ? `Slettes ${dateText(d.retentionUntil)}` : null].filter(Boolean).join(' · ')}>
      <CardBody>
        <p><Btn ghost onClick={onClose}>Lukk</Btn></p>
        {!current && <p className="msg warn">Dette er en eldre versjon (versjon {d.version}). Den gjeldende er øverst i versjonslisten.</p>}
        {d.description && <p style={{ whiteSpace: 'pre-line' }}>{d.description}</p>}
        {d.kind === 'note' && !edit && <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: '8px 0' }}>{d.noteText}</pre>}
        {d.kind === 'link' && !edit && <p><a href={d.url} target="_blank" rel="noopener noreferrer">{d.url}</a></p>}
        {d.kind === 'file' && <p>{d.filename} · {sizeText(d.sizeBytes)} · <Btn ghost onClick={() => get(d)}>Last ned</Btn></p>}

        {can.manage && current && !edit && (
          <div className="work-bar">
            {!d.archivedAt && <Btn ghost onClick={() => setEdit({ title: d.title, description: d.description || '', folderId: d.folderId || '', noteText: d.noteText || '', url: d.url || '' })}>Endre</Btn>}
            {d.kind === 'file' && !d.archivedAt && (
              <DropZone title={busy ? 'Laster opp …' : 'Last opp ny versjon'} accept={ACCEPT} onFile={replace}><span className="note"> Den gamle beholdes i versjonslisten.</span></DropZone>
            )}
            {d.archivedAt
              ? <Btn ghost onClick={() => act('restore')}>Hent tilbake</Btn>
              : <TwoStep label="Arkiver" busyLabel="Arkiverer …" confirmLabel="Klikk igjen: dokumentet skjules, og delte lenker slutter å virke" onConfirm={() => act('archive')} />}
          </div>
        )}

        {edit && (
          <>
            <div className="row">
              <Field label="Tittel" required className="field half">{(fid) => <input id={fid} maxLength={160} value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} />}</Field>
              <Field label="Mappe" className="field half">{(fid) => (
                <select id={fid} value={edit.folderId} onChange={(e) => setEdit({ ...edit, folderId: e.target.value })}>
                  <option value="">(ingen mappe)</option>
                  {folderOptions(tree, undefined, maxDepth).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>)}</Field>
            </div>
            {d.kind === 'note' && <div className="row"><Field label="Notat" required className="field">{(fid) => (
              <textarea id={fid} rows={12} maxLength={20000} value={edit.noteText} onChange={(e) => setEdit({ ...edit, noteText: e.target.value })} />)}</Field></div>}
            {d.kind === 'link' && <div className="row"><Field label="Adresse" required className="field">{(fid) => (
              <input id={fid} type="url" maxLength={2000} value={edit.url} onChange={(e) => setEdit({ ...edit, url: e.target.value })} />)}</Field></div>}
            <div className="row"><Field label="Beskrivelse" className="field">{(fid) => (
              <textarea id={fid} rows={2} maxLength={1000} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} />)}</Field></div>
            <div className="work-bar"><Btn disabled={busy} onClick={saveEdit}>{busy ? 'Lagrer …' : 'Lagre'}</Btn><Btn ghost onClick={() => setEdit(null)}>Avbryt</Btn></div>
          </>
        )}
        <Msg text={msg.text} tone={msg.tone} />

        {d.kind === 'file' && data.versions && data.versions.length > 0 && (
          <>
            <h3>Versjoner</h3>
            <table className="t">
              <thead><tr><th scope="col">Versjon</th><th scope="col">Fil</th><th scope="col">Størrelse</th><th scope="col">Lastet opp</th><th scope="col"><span className="vh">Handling</span></th></tr></thead>
              <tbody>{data.versions.map((v: Body) => (
                <tr key={v.id}><td>{v.version}{v.id === data.current && <> <Tag tone="green">Gjeldende</Tag></>}</td><td>{v.filename}</td><td>{sizeText(v.sizeBytes)}</td>
                  <td>{dateText(v.createdAt)}</td><td><Btn ghost aria-label={`Last ned versjon ${v.version}`} onClick={() => get(v)}>Last ned</Btn></td></tr>))}</tbody>
            </table>
          </>
        )}

        {can.share && d.kind === 'file' && (
          <>
            <h3>Delte lenker</h3>
            <DocumentShares documentId={d.id} canMake={current && !d.archivedAt} />
          </>
        )}
      </CardBody>
    </Card>
  );
}

/* ---------------------------------------------------------------- the view ---------------------------------------------------------------- */

export function Dokumenter() {
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  const [folder, setFolder] = useState('');            // '' every folder, 'root' no folder, or an id
  const [archived, setArchived] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [newForm, setNewForm] = useState<NewForm | null>(null);
  const [folderForm, setFolderForm] = useState<FolderForm | null>(null);
  const [msg, say] = useMsg();
  useEffect(() => { const t = setTimeout(() => setQ(typed.trim()), 250); return () => clearTimeout(t); }, [typed]);
  const qs = new URLSearchParams({ archived: archived ? 'all' : 'no', ...(folder ? { folder } : {}), ...(q ? { q } : {}) });
  const folders = useLoad<Body>('/documents/folders');
  const list = useLoad<Body>('/documents?' + qs.toString(), [qs.toString()]);
  const reloadAll = (m?: string) => { if (m) say(m, 'ok'); folders.reload(); list.reload(); };

  const tree: Body[] = (folders.data && folders.data.tree) || [];
  const maxDepth: number = (folders.data && folders.data.maxDepth) || 3;
  const flat: Body[] = (folders.data && folders.data.folders) || [];
  const can: Body = (list.data && list.data.can) || (folders.data && folders.data.can) || {};
  const items: Body[] = (list.data && list.data.items) || [];
  const chosen = flat.find((f) => f.id === folder);
  const startNew = (kind: NewForm['kind']) => setNewForm({ kind, title: '', description: '', folderId: chosen ? chosen.id : '', noteText: '', url: '', file: null });
  const deleteFolder = async () => {
    if (!chosen) return;
    const r = await send(`/documents/folders/${chosen.id}`, { method: 'DELETE' });
    if (!r.ok) return say(errText(r.body), 'err');
    setFolder(''); reloadAll(`Mappen «${chosen.name}» er slettet.`);
  };

  return (
    <div className={'work-split' + (open ? ' has-detail' : '')}>
      <Card className="work-list" title="Dokumenter"
            hint="Moskeens eget arkiv for ansatte og styret: filer, notater og lenker. Filene lagres kryptert. Ingenting vises for medlemmer, og ingenting deles uten en lenke som utløper.">
        <div className="work-bar">
          <Field label="Søk" className="field work-search">{(id) => (
            <input id={id} type="search" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Tittel, beskrivelse, notat eller filnavn" maxLength={100} />
          )}</Field>
          <label className="field note" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} /> Vis arkiverte
          </label>
        </div>
        {can.manage && (
          <div className="work-bar">
            <Btn onClick={() => startNew('file')}>+ Last opp fil</Btn>
            <Btn ghost onClick={() => startNew('note')}>+ Nytt notat</Btn>
            <Btn ghost onClick={() => startNew('link')}>+ Ny lenke</Btn>
          </div>
        )}
        <CardBody><Msg text={msg.text} tone={msg.tone} /></CardBody>
        <div className="docs-split">
          <nav aria-label="Mapper">
            <button type="button" aria-pressed={folder === ''} onClick={() => setFolder('')}
                    style={{ fontWeight: folder === '' ? 700 : 400, background: 'none', border: 0, padding: '3px 0', cursor: 'pointer', color: 'inherit' }}>Alle dokumenter</button>
            <br />
            <button type="button" aria-pressed={folder === 'root'} onClick={() => setFolder('root')}
                    style={{ fontWeight: folder === 'root' ? 700 : 400, background: 'none', border: 0, padding: '3px 0', cursor: 'pointer', color: 'inherit' }}>Uten mappe</button>
            {folders.error ? <p className="msg err">{folders.error}</p> : !folders.data ? <Loading /> : <Tree nodes={tree} folder={folder} pick={setFolder} />}
            {can.manage && (
              <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                <Btn ghost onClick={() => setFolderForm({ id: null, name: '', parentId: chosen && chosen.depth < maxDepth ? chosen.id : '', retentionMonths: '' })}>+ Mappe</Btn>
                {chosen && <Btn ghost onClick={() => setFolderForm({ id: chosen.id, name: chosen.name, parentId: chosen.parentId || '', retentionMonths: chosen.retentionMonths ? String(chosen.retentionMonths) : '' })}>Endre mappe</Btn>}
                {chosen && <TwoStep label="Slett mappe" busyLabel="Sletter …" confirmLabel="Klikk igjen: bare en tom mappe kan slettes" onConfirm={deleteFolder} />}
              </div>
            )}
            {chosen && chosen.retentionMonths && <p className="note">Oppbevaring: {chosen.retentionMonths} måneder.</p>}
          </nav>
          <div className="docs-list">
            {list.error ? <LoadError error={list.error} retry={list.reload} /> : !list.data ? <Loading /> : (
              <>
                <p className="vh" role="status">{items.length === 1 ? '1 dokument' : `${items.length} dokumenter`}</p>
                <table className="t">
                  <thead><tr><th scope="col">Tittel</th><th scope="col">Type</th><th scope="col">Endret</th><th scope="col"><span className="vh">Åpne</span></th></tr></thead>
                  <tbody>{items.length ? items.map((x) => (
                    <tr key={x.id}>
                      <td><b>{x.title}</b>{x.archivedAt && <> <Tag tone="grey">Arkivert</Tag></>}{x.version > 1 && <> <Tag tone="gold">v{x.version}</Tag></>}
                        {x.description && <div className="sub">{x.description}</div>}
                        {x.kind === 'file' && <div className="sub">{x.filename} · {sizeText(x.sizeBytes)}</div>}</td>
                      <td>{KIND_NO[x.kind] || x.kind}</td>
                      <td>{dateText(x.updatedAt)}</td>
                      <td><Btn ghost aria-label={`Åpne ${x.title}`} onClick={() => setOpen(x.id)}>Åpne</Btn></td>
                    </tr>)) : <tr><td colSpan={4}>{q ? 'Ingen dokumenter passer søket.' : can.manage ? 'Ingen dokumenter her ennå.' : 'Ingen dokumenter her.'}</td></tr>}</tbody>
                </table>
                {!archived && list.data.archivedCount > 0 && <CardBody><p className="note">{list.data.archivedCount === 1 ? '1 arkivert dokument er skjult.' : `${list.data.archivedCount} arkiverte dokumenter er skjult.`}</p></CardBody>}
              </>
            )}
          </div>
        </div>
      </Card>
      {open && <Detail id={open} tree={tree} maxDepth={maxDepth} onClose={() => setOpen(null)} onChanged={(m) => reloadAll(m)} />}
      <NewDialog form={newForm} setForm={setNewForm} tree={tree} maxDepth={maxDepth} onDone={(m, id) => { reloadAll(m); if (id) setOpen(id); }} />
      <FolderDialog form={folderForm} setForm={setFolderForm} tree={tree} maxDepth={maxDepth} onDone={(m) => reloadAll(m)} />
    </div>
  );
}
